// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ProductQuantityBreakPricingStyles;
}

export interface ProductQuantityBreakPricingStyles {
    viewLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    breakQty?: TypographyPresentationProps;
    breakPrice?: TypographyPresentationProps;
    note?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    row?: InjectableCss;
    breakUOM?: TypographyPresentationProps;
}

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    modal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    breakQtyHeader: { alignX: "center" },
    breakPriceHeader: { alignX: "center" },
    savingsHeader: { alignX: "center" },
    breakQtyCell: { alignX: "center" },
    breakPriceCell: { alignX: "center" },
    savingsCell: { alignX: "center" },
};

const ProductQuantityBreakPricing: FC<OwnProps & HasProduct> = ({
    product,
    productInfo: {
        pricing,
        // XNG-Changes
        unitOfMeasure,
    },
    extendedStyles,
}) => {
    const showPricing = useAppSelector(state => state.context.showPricing);
    // const [modalIsOpen, setModalIsOpen] = React.useState(false);
    // const modalCloseHandler = () => {
    //     setModalIsOpen(false);
    // };
    // const viewLinkClickHandler = () => {
    //     setModalIsOpen(true);
    // };

    const [styles] = React.useState(() => mergeToNew(productQuantityBreakPricingStyles, extendedStyles));

    if (
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2 ||
        !showPricing
    ) {
        return null;
    }

    // <XNG-Changes>
    const unitOfMeasureDescription = product.unitOfMeasures?.find(o => o.unitOfMeasure === unitOfMeasure)?.description;
    const unitOfMeasureDisplay = product.unitOfMeasures?.find(
        o => o.unitOfMeasure === unitOfMeasure,
    )?.unitOfMeasureDisplay;
    // </XNG-Changes>

    // XNG-Change: remove modal and change structure to match design
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title} data-test-selector="quantityBreakPricingTitle">
                {translate("Buy More & Save")}
            </Typography>
            {pricing!.unitRegularBreakPrices!.map(breakPrice => (
                <StyledWrapper {...styles.row} key={breakPrice.breakQty} data-test-selector="breakPriceRow">
                    <Typography {...styles.breakQty} data-test-selector="breakQty">
                        {breakPrice.savingsMessage}
                    </Typography>
                    <Typography {...styles.breakPrice} data-test-selector="price">
                        {breakPrice.breakPriceDisplay}
                        <Typography {...styles.breakUOM}>
                            / {unitOfMeasureDescription || unitOfMeasureDisplay}
                        </Typography>
                    </Typography>
                </StyledWrapper>
            ))}
            <Typography {...styles.note}>{translate("Bulk purchasing price is based on sale price.")}</Typography>
        </StyledWrapper>
    );
};

export default withProduct(ProductQuantityBreakPricing);
